import React, { useCallback, useState } from 'react';

import Api from '~/Api';
import { useModalContext } from '~/contexts/modal/ModalContext';
import useErrors from '~/hooks/useErrors';
import UpgradeSubscriptionCheckout, {
  singleScreenModalConfig,
} from '~/modules/settings/components/modals/UpgradeSubscriptionCheckout/variants/SingleScreen';
import analytics from '~/services/analytics';
import { useActions } from '~/store/hooks/useActions';
import { ProductType } from '~/types/product';
import { timeout } from '~/utils/common';

import { BlackFridayProductId, BlackFridayPurchaseSource } from '../constants';

const useBlackFriday = (): {
  handleBlackFridayClick: () => Promise<void>;
  prefetchProductById: () => Promise<void>;
} => {
  const { handleOpenModal } = useModalContext();
  const { loadAndSetActualProfileData } = useActions();
  const { reportUserErrors, reportProfileError } = useErrors();

  const [prefetchedProduct, setPrefetchedProduct] =
    useState<ProductType | null>(null);

  const prefetchProductById = useCallback(async (): Promise<void> => {
    if (prefetchedProduct) {
      return;
    }

    try {
      const productById = await Api.getProductById(BlackFridayProductId);
      setPrefetchedProduct(productById);
    } catch (e) {
      reportUserErrors({
        error: e,
        method: 'getProductById',
        userMessage: 'Unable to fetch product details. Please retry or',
      });
    }
  }, [prefetchedProduct]);

  const handleBlackFridayClick = async (): Promise<void> => {
    try {
      const productById =
        prefetchedProduct || (await Api.getProductById(BlackFridayProductId));

      handleOpenModal({
        onClose: () => {
          analytics.trackEvent('platform checkout - close');
        },
        onShow: () => {
          analytics.trackEvent('black friday - promo checkout');
        },
        ...singleScreenModalConfig,
        component: ({ onClose }) => (
          <UpgradeSubscriptionCheckout
            product={productById}
            purchaseSource={BlackFridayPurchaseSource}
            onSuccessPayment={async (): Promise<void> => {
              try {
                await timeout(4000);
                await loadAndSetActualProfileData();
              } catch (e: any) {
                reportProfileError(e);
              } finally {
                onClose();
              }
            }}
            orderDescriptionProps={{
              annualPlanDiscount: '400 USD',
              annualPlanOldPrice: '480 USD',
            }}
          />
        ),
      });

      if (!prefetchedProduct) {
        setPrefetchedProduct(productById);
      }
    } catch (e) {
      reportUserErrors({
        error: e,
        method: 'getProductById',
        userMessage: 'Unable to fetch product details. Please retry or',
      });
    }
  };

  return { handleBlackFridayClick, prefetchProductById };
};

export default useBlackFriday;
